import { useState, useEffect } from 'react'
import styled from '@emotion/styled'
import { t } from '@lingui/core/macro'
import AddIcon from '@mui/icons-material/Add'
import CachedIcon from '@mui/icons-material/Cached'
import { Tooltip } from '@mui/material'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import { useDispatch } from 'react-redux'
import { CvsTextField } from '@components'
import * as SnackbarActions from '../../../global/snackbar/snackbar.actions'
import { DependentMappings } from '../field-mapperV2'
import { IntegrationMappableField, IntegrationMappingField, TemplateMappingOption } from '../utils'
import { FieldMappingReset } from './field-mapping-reset'

const GASchemaContainer = styled.div`
  margin-bottom: 15px;
  display: flex;
  justify-content: flex-start;
  width: 100%;
`

const AddMappingContainer = styled.div`
  margin-top: 20px;
`

interface Props {
  mappings: IntegrationMappingField[]
  templateOptions: TemplateMappingOption[]
  addRow: () => void
  integrationTitle: string
  integrationOptions: IntegrationMappableField[]
  onIntegrationChange?: (schema: any, mappings: IntegrationMappingField[]) => void
  integration: any
  mappingsDisabled?: boolean
  dependentMappings?: DependentMappings
}

export const MappingControl = ({
  mappings,
  templateOptions,
  addRow,
  integrationTitle,
  integrationOptions,
  onIntegrationChange,
  integration,
  mappingsDisabled,
  dependentMappings,
}: Props) => {
  const [addMappingsIsDisabled, setAddMappingsIsDisabled] = useState(false)
  const [tooltipTitle, setTooltipTitle] = useState('')
  const dispatch = useDispatch()

  useEffect(() => {
    const filteredTemplateOptions = templateOptions.filter(
      option => option.label !== t`Claravine ID` && option.label !== t`Deep Link`,
    )

    let title = t`No available template fields. Add more fields to this template to add more mappings.`
    if (
      integrationOptions.filter(i => !i.required).length === 0 ||
      integrationOptions.length === mappings.length
    ) {
      title = t`No available ${integrationTitle} fields.`
    }

    const mappingShouldBeDisabled =
      integrationOptions.filter(i => !i.required).length === 0 || mappings !== undefined
        ? mappings.length >= filteredTemplateOptions.length ||
          integrationOptions.length === mappings.length
        : false
    setAddMappingsIsDisabled(mappingShouldBeDisabled)
    setTooltipTitle(title)
  }, [mappings, templateOptions])

  const generateMappingsFromSchema = () => {
    const gaMappings = integration?.metadata?.schema.trim().split(',')
    let invalidMapping = null
    const newMappings: IntegrationMappingField[] = []
    gaMappings.forEach(m => {
      const mapping = integrationOptions.find(i => i.fieldName === m)
      if (mapping) {
        const newMapping: IntegrationMappingField = {
          required: true,
          cvFieldId: '',
          fieldName: m,
          type: 'integration_field',
          fieldSourceType: 'field',
        }
        newMappings.push(newMapping)
      } else {
        invalidMapping = m
      }
    })
    if (invalidMapping) {
      dispatch(
        SnackbarActions.showSnackbar({
          message: t`The key '${invalidMapping}' within your GA Schema is not supported. If your schema was pasted directly from Google Analytics, reach out to your CSM about adding support.`,
          type: 'ERROR',
        }),
      )
      return
    }
    // add schema to the integration metadata and create mappings
    if (onIntegrationChange) {
      onIntegrationChange({ schema: integration?.metadata?.schema.trim() }, newMappings)
    }
  }

  const resetMappingsAndSchema = () => {
    if (onIntegrationChange) {
      onIntegrationChange({ schema: '' }, [])
    }
  }

  const IntegrationSpecificMapping = () => {
    if (mappingsDisabled && dependentMappings) {
      return <FieldMappingReset resetMappings={dependentMappings.resetFieldMappings} />
    }

    switch (integrationTitle) {
      case 'Google Analytics':
        return (
          <>
            {!mappings.length ? (
              <>
                <GASchemaContainer>
                  <Grid item xs={12}>
                    <GASchemaInput
                      value={integration?.metadata?.schema}
                      handleInput={input => {
                        onIntegrationChange?.({ schema: input }, [])
                      }}
                    />
                  </Grid>
                </GASchemaContainer>
                <div
                  style={{
                    marginBottom: 10,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Tooltip
                    title={
                      integration?.metadata?.schema !== ''
                        ? ''
                        : 'Add a schema to the text field above.'
                    }
                    placement="right"
                  >
                    <span>
                      <Button
                        disabled={integration?.metadata?.schema === ''}
                        color="primary"
                        onClick={generateMappingsFromSchema}
                        variant="outlined"
                      >
                        {t`Apply schema`}
                      </Button>
                    </span>
                  </Tooltip>
                </div>
              </>
            ) : (
              <div
                style={{
                  marginBottom: 10,
                  marginTop: 20,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Tooltip
                  title={t`This will remove all field mappings and a new schema will need to be set.`}
                  placement="left"
                >
                  <span>
                    <Button
                      startIcon={<CachedIcon />}
                      color="primary"
                      onClick={resetMappingsAndSchema}
                      variant="outlined"
                    >
                      {t`Reset field mapping`}
                    </Button>
                  </span>
                </Tooltip>
              </div>
            )}
          </>
        )
      default:
        return (
          <AddMappingContainer>
            <Tooltip title={addMappingsIsDisabled ? tooltipTitle : ''} placement="right">
              <span>
                <Button
                  disabled={addMappingsIsDisabled}
                  startIcon={<AddIcon />}
                  color="primary"
                  onClick={addRow}
                  variant="contained"
                >
                  {t`Add new mapping`}
                </Button>
              </span>
            </Tooltip>
          </AddMappingContainer>
        )
    }
  }

  return IntegrationSpecificMapping()
}

const GASchemaInput = ({ value, handleInput }) => (
  <CvsTextField
    label={t`Data set schema`}
    style={{ width: '100%' }}
    multiline={true}
    placeholder={t`Paste in your data set schema from Google Analytics as a comma separated list (e.g. ga:dimension17, ga:dimension18, ga:dimension19)`}
    value={value}
    onChange={e => {
      handleInput(e.target.value)
    }}
  />
)
